import {
  CUSTOMER_COHORT_LABELS,
  classifyRfmBucket,
  matchesCustomerCohort,
  parseCustomerCohort,
  RFM_BUCKET_LABELS,
  type CustomerCohortFilter,
  type RfmBucket,
} from "@/lib/customer-360";
import { ORDER_STATUS_LABELS } from "@/domain/sm-models";
import type { OrderStatus } from "@/domain/sm-models";
import { Badge } from "@/components/ui/badge";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { businessIsServicesNiche } from "@/lib/business-profile";
import { toast } from "sonner";
import { ExternalLink, UserPlus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClinicalPageShell } from "@/components/clinical";
import { DashboardCard, DataTable, Loading, MERCHANT_HUB_GRID } from "@/components/ds";
import { CrudShellHeader, CrudEmptyState } from "@/components/ds/CrudShell";
import type { DataTableColumn } from "@/components/ds";
import { WorkspaceHubBack } from "@/components/workspace/WorkspaceHub";
import { CLIENTES_HUB_LINKS } from "@/lib/workspace-hubs";
import { InlineCustomerAi } from "@/components/renata/InlineCustomerAi";
import type { ClientPack, SessionPack } from "@/lib/db-service/service-packs";
import type { CustomerContact, CustomerContactType, CustomerAddress, CustomerAddressType } from "@/lib/db-service/customers";
import type { WaitlistEntry } from "@/lib/db-service/service-waitlist";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { ClientesTour } from "@/components/ProductTour";

const CONTACT_TYPE_LABELS: Record<CustomerContactType, string> = {
  vendedor: "Vendedor",
  propietario: "Propietario",
  administracion: "Administración",
  facturacion: "Facturación",
  envios: "Envíos",
  otro: "Otro",
};

const ADDRESS_TYPE_LABELS: Record<CustomerAddressType, string> = {
  fiscal: "Fiscal",
  envio: "Envío",
  facturacion: "Facturación",
  otro: "Otro",
};

export const Route = createFileRoute("/app/clientes")({
  validateSearch: (s: Record<string, unknown>) => {
    const v = typeof s.vista === "string" ? s.vista : "hub";
    const vista = v === "b2c" || v === "b2b" ? v : "hub";
    const detalle = typeof s.detalle === "string" && s.detalle.trim() ? s.detalle.trim() : undefined;
    const cohort = parseCustomerCohort(s.cohort);
    const nuevo = s.nuevo === "1" || s.nuevo === true;
    return { vista, detalle, cohort, nuevo: nuevo || undefined };
  },
  component: ClientesPage,
  head: () => ({ meta: [{ title: "Clientes — Structura Market" }] }),
});

interface StoreCustomer {
  contactKey: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  orderCount: number;
  lastOrderAt: string;
  firstOrderAt: string;
}

interface InstitutionalUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  institutionName: string | null;
  channelId: string | null;
  channelName: string | null;
  status: string;
  createdAt: string;
}

interface B2bChannelOption {
  id: string;
  name: string;
}


interface Customer360 {
  contactKey: string;
  profile: {
    name: string | null;
    email: string | null;
    phone: string | null;
    customerAccountId: string | null;
    loyaltyOptIn: boolean;
    internalNotes: string | null;
  };
  orderCount: number;
  revenueLifetime: number;
  lastOrderAt: string | null;
  firstOrderAt: string | null;
  daysSinceLastOrder: number | null;
  rfmBucket: RfmBucket;
  rfmLabel: string;
  loyaltyPoints: number | null;
  lastNpsScore?: number | null;
  lastNpsAt?: string | null;
  reviewLinkClicks?: number;
  lastReviewLinkClickAt?: string | null;
  recentOrders: Array<{ id: string; status: string; createdAt: string; total: number }>;
  recentInvoices: Array<{ id: string; number: string | null; type: string | null; total: number | null; createdAt: string; pdfUrl: string | null }>;
}

function fmtMoney(n: number) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(n);
}

function Customer360Panel({
  data,
  onBack,
}: {
  data: Customer360;
  onBack: () => void;
}) {
  const tenantId = useWorkspaceTenantId();
  const { businessProfile } = useTenantContext();
  const showTurnos = businessIsServicesNiche(businessProfile);
  const [turnos, setTurnos] = useState<Array<{
    id: string;
    bookingDate: string;
    startTime: string;
    status: string;
    serviceName?: string;
    professionalName?: string;
  }>>([]);

  useEffect(() => {
    if (!showTurnos || !tenantId || !data.profile.email) return;
    void apiGetJson<{ bookings: typeof turnos }>(
      `/api/service-bookings?customerEmail=${encodeURIComponent(data.profile.email)}`,
      tenantId,
    )
      .then((d) => setTurnos(d.bookings ?? []))
      .catch(() => setTurnos([]));
  }, [showTurnos, tenantId, data.profile.email]);

  // SCRUM-487: la ficha nunca mostraba saldo de cuenta corriente pese a ser
  // parte del AC original (insights + acceso directo) — el hub tenía una
  // pantalla separada con su propia lista, sin ningún link desde acá.
  const [currentAccount, setCurrentAccount] = useState<{ enabled: boolean; balance: number } | null>(null);
  useEffect(() => {
    if (!tenantId || (!data.profile.email && !data.profile.phone)) return;
    const qs = data.profile.email
      ? `email=${encodeURIComponent(data.profile.email)}`
      : `phone=${encodeURIComponent(data.profile.phone!)}`;
    void apiGetJson<{ enabled: boolean; balance: number }>(
      `/api/customers/current-account?${qs}&partyType=b2c`,
      tenantId,
    )
      .then((a) => setCurrentAccount({ enabled: a.enabled, balance: a.balance }))
      .catch(() => setCurrentAccount(null));
  }, [tenantId, data.profile.email, data.profile.phone]);

  // SCRUM-487 sección 4: "Suma puntos loyalty" — atributo nuevo, independiente
  // del toggle de programa a nivel tenant y del tipo B2B/B2C del cliente.
  const [loyaltyOptIn, setLoyaltyOptIn] = useState(data.profile.loyaltyOptIn);
  const [savingLoyaltyOptIn, setSavingLoyaltyOptIn] = useState(false);
  const toggleLoyaltyOptIn = async (next: boolean) => {
    if (!tenantId) return;
    setSavingLoyaltyOptIn(true);
    const prev = loyaltyOptIn;
    setLoyaltyOptIn(next);
    try {
      await apiSendJson("/api/customers", "PATCH", {
        email: data.profile.email, phone: data.profile.phone, customerName: data.profile.name,
        loyaltyOptIn: next,
      }, tenantId);
    } catch (e) {
      setLoyaltyOptIn(prev);
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar loyalty");
    } finally {
      setSavingLoyaltyOptIn(false);
    }
  };

  // SCRUM-487 sección 5: notas internas — visibles solo para el equipo.
  const [internalNotes, setInternalNotes] = useState(data.profile.internalNotes ?? "");
  const [savingNotes, setSavingNotes] = useState(false);
  const saveInternalNotes = async () => {
    if (!tenantId) return;
    setSavingNotes(true);
    try {
      await apiSendJson("/api/customers", "PATCH", {
        email: data.profile.email, phone: data.profile.phone, customerName: data.profile.name,
        internalNotes,
      }, tenantId);
      toast.success("Notas guardadas");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudieron guardar las notas");
    } finally {
      setSavingNotes(false);
    }
  };

  // SCRUM-487 sección 6: contactos múltiples por cliente (antes no existía
  // ni el modelo de datos ni la UI).
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [contactCustomerId, setContactCustomerId] = useState<string | null>(null);
  const loadContacts = useCallback(() => {
    if (!tenantId || (!data.profile.email && !data.profile.phone)) return;
    const qs = data.profile.email
      ? `email=${encodeURIComponent(data.profile.email)}`
      : `phone=${encodeURIComponent(data.profile.phone!)}`;
    void apiGetJson<{ contacts: CustomerContact[]; customerId: string | null }>(`/api/customers/contacts?${qs}`, tenantId)
      .then((r) => {
        setContacts(r.contacts ?? []);
        setContactCustomerId(r.customerId ?? null);
      })
      .catch(() => setContacts([]));
  }, [tenantId, data.profile.email, data.profile.phone]);
  useEffect(() => loadContacts(), [loadContacts]);

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CustomerContact | null>(null);
  const [contactForm, setContactForm] = useState<{
    name: string; lastName: string; email: string; phone: string;
    contactType: CustomerContactType; isPrimary: boolean;
  }>({ name: "", lastName: "", email: "", phone: "", contactType: "otro", isPrimary: false });
  const [savingContact, setSavingContact] = useState(false);

  const openNewContactDialog = () => {
    setEditingContact(null);
    setContactForm({ name: "", lastName: "", email: "", phone: "", contactType: "otro", isPrimary: contacts.length === 0 });
    setContactDialogOpen(true);
  };
  const openEditContactDialog = (c: CustomerContact) => {
    setEditingContact(c);
    setContactForm({
      name: c.name, lastName: c.lastName ?? "", email: c.email ?? "", phone: c.phone ?? "",
      contactType: c.contactType, isPrimary: c.isPrimary,
    });
    setContactDialogOpen(true);
  };

  const saveContact = async () => {
    if (!tenantId || !contactForm.name.trim()) return;
    setSavingContact(true);
    try {
      if (editingContact) {
        if (!contactCustomerId) return;
        await apiSendJson("/api/customers/contacts", "PATCH", {
          customerId: contactCustomerId,
          contactId: editingContact.id,
          name: contactForm.name, lastName: contactForm.lastName || null,
          contactEmail: contactForm.email || null, contactPhone: contactForm.phone || null,
          contactType: contactForm.contactType, isPrimary: contactForm.isPrimary,
        }, tenantId);
      } else {
        const res = await apiSendJson<{ customerId: string }>("/api/customers/contacts", "POST", {
          email: data.profile.email, phone: data.profile.phone, customerName: data.profile.name,
          name: contactForm.name, lastName: contactForm.lastName || null,
          contactEmail: contactForm.email || null, contactPhone: contactForm.phone || null,
          contactType: contactForm.contactType, isPrimary: contactForm.isPrimary,
        }, tenantId);
        setContactCustomerId(res.customerId);
      }
      toast.success(editingContact ? "Contacto actualizado" : "Contacto agregado");
      setContactDialogOpen(false);
      loadContacts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar el contacto");
    } finally {
      setSavingContact(false);
    }
  };

  const deleteContact = async (c: CustomerContact) => {
    if (!tenantId || !contactCustomerId) return;
    try {
      await apiSendJson(`/api/customers/contacts?customerId=${encodeURIComponent(contactCustomerId)}&contactId=${encodeURIComponent(c.id)}`, "DELETE", undefined, tenantId);
      toast.success("Contacto eliminado");
      loadContacts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo eliminar el contacto");
    }
  };

  // SCRUM-487 sección 7: direcciones en la ficha merchant. El backend ya
  // existía (usado por el account del storefront) — acá solo se reutiliza
  // y se le agrega el tipo (Fiscal/Envío/Facturación/Otro) que pedía el AC.
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [addressCustomerId, setAddressCustomerId] = useState<string | null>(null);
  const loadAddresses = useCallback(() => {
    if (!tenantId || (!data.profile.email && !data.profile.phone)) return;
    const qs = data.profile.email
      ? `email=${encodeURIComponent(data.profile.email)}`
      : `phone=${encodeURIComponent(data.profile.phone!)}`;
    void apiGetJson<{ addresses: CustomerAddress[]; customerId: string | null }>(`/api/customers/addresses?${qs}`, tenantId)
      .then((r) => {
        setAddresses(r.addresses ?? []);
        setAddressCustomerId(r.customerId ?? null);
      })
      .catch(() => setAddresses([]));
  }, [tenantId, data.profile.email, data.profile.phone]);
  useEffect(() => loadAddresses(), [loadAddresses]);

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [addressForm, setAddressForm] = useState<{
    street: string; streetNumber: string; floor: string; apartment: string;
    city: string; province: string; postalCode: string;
    label: string; notes: string; addressType: CustomerAddressType; isDefault: boolean;
  }>({ street: "", streetNumber: "", floor: "", apartment: "", city: "", province: "", postalCode: "", label: "", notes: "", addressType: "otro", isDefault: false });
  const [savingAddress, setSavingAddress] = useState(false);

  const openNewAddressDialog = () => {
    setEditingAddress(null);
    setAddressForm({ street: "", streetNumber: "", floor: "", apartment: "", city: "", province: "", postalCode: "", label: "", notes: "", addressType: "otro", isDefault: addresses.length === 0 });
    setAddressDialogOpen(true);
  };
  const openEditAddressDialog = (a: CustomerAddress) => {
    setEditingAddress(a);
    setAddressForm({
      street: a.street ?? "", streetNumber: a.streetNumber ?? "", floor: a.floor ?? "", apartment: a.apartment ?? "",
      city: a.city ?? "", province: a.province ?? "", postalCode: a.postalCode ?? "",
      label: a.label ?? "", notes: a.notes ?? "", addressType: a.addressType, isDefault: a.isDefault,
    });
    setAddressDialogOpen(true);
  };

  const saveAddress = async () => {
    if (!tenantId) return;
    setSavingAddress(true);
    try {
      const payload = {
        street: addressForm.street, streetNumber: addressForm.streetNumber,
        floor: addressForm.floor || null, apartment: addressForm.apartment || null,
        city: addressForm.city, province: addressForm.province, postalCode: addressForm.postalCode,
        label: addressForm.label || null, notes: addressForm.notes || null,
        addressType: addressForm.addressType, isDefault: addressForm.isDefault,
      };
      if (editingAddress) {
        if (!addressCustomerId) return;
        await apiSendJson("/api/customers/addresses", "PATCH", {
          customerId: addressCustomerId, addressId: editingAddress.id, ...payload,
        }, tenantId);
      } else {
        const res = await apiSendJson<{ customerId: string }>("/api/customers/addresses", "POST", {
          email: data.profile.email, phone: data.profile.phone, customerName: data.profile.name, ...payload,
        }, tenantId);
        setAddressCustomerId(res.customerId);
      }
      toast.success(editingAddress ? "Dirección actualizada" : "Dirección agregada");
      setAddressDialogOpen(false);
      loadAddresses();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar la dirección");
    } finally {
      setSavingAddress(false);
    }
  };

  const deleteAddress = async (a: CustomerAddress) => {
    if (!tenantId || !addressCustomerId) return;
    try {
      await apiSendJson(`/api/customers/addresses?customerId=${encodeURIComponent(addressCustomerId)}&addressId=${encodeURIComponent(a.id)}`, "DELETE", undefined, tenantId);
      toast.success("Dirección eliminada");
      loadAddresses();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo eliminar la dirección");
    }
  };

  // SCRUM-409: packs y asignación — backend ya existía completo
  // (dbSmAssignClientPack), solo faltaba mostrarlo en la ficha del cliente.
  const [clientPacks, setClientPacks] = useState<ClientPack[]>([]);
  const [sessionPacks, setSessionPacks] = useState<SessionPack[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string>("");
  const [assigningPack, setAssigningPack] = useState(false);
  // Puede resolverse recién al asignar el primer pack (cliente sin fila en
  // sm_customers todavía, ej. solo reservó turnos) — no depende únicamente
  // de data.profile, que no se refresca sola tras la asignación.
  const [resolvedClientId, setResolvedClientId] = useState<string | null>(data.profile.customerAccountId);

  const loadClientPacks = (clientId: string | null) => {
    if (!showTurnos || !tenantId || !clientId) return;
    void apiGetJson<{ items: ClientPack[] }>(
      `/api/service-admin?resource=client-packs&clientId=${encodeURIComponent(clientId)}`,
      tenantId,
    )
      .then((r) => setClientPacks(r.items ?? []))
      .catch(() => setClientPacks([]));
  };

  useEffect(() => {
    loadClientPacks(resolvedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTurnos, tenantId, resolvedClientId]);

  useEffect(() => {
    if (!showTurnos || !tenantId) return;
    void apiGetJson<{ items: SessionPack[] }>("/api/service-admin?resource=packs", tenantId)
      .then((r) => setSessionPacks((r.items ?? []).filter((p) => p.isActive)))
      .catch(() => setSessionPacks([]));
  }, [showTurnos, tenantId]);

  // SCRUM-409: visibilidad de lista de espera desde la ficha — la gestión
  // completa ya vive en /app/lista-espera (333 líneas, CRUD real); acá solo
  // se muestra si ESTE cliente tiene una entrada activa, sin duplicar esa
  // pantalla.
  const [waitlistEntry, setWaitlistEntry] = useState<WaitlistEntry | null>(null);
  useEffect(() => {
    if (!showTurnos || !tenantId || !data.profile.email) return;
    void apiGetJson<{ items: WaitlistEntry[] }>("/api/service-admin?resource=waitlist", tenantId)
      .then((r) => {
        const email = data.profile.email!.trim().toLowerCase();
        const hit = (r.items ?? []).find(
          (w) => w.customerEmail.trim().toLowerCase() === email && w.status === "waiting",
        );
        setWaitlistEntry(hit ?? null);
      })
      .catch(() => setWaitlistEntry(null));
  }, [showTurnos, tenantId, data.profile.email]);

  const assignPack = async () => {
    if (!tenantId || !selectedPackId) return;
    setAssigningPack(true);
    try {
      const res = await apiSendJson<{ item: ClientPack }>(
        "/api/service-admin?resource=client-packs",
        "POST",
        resolvedClientId
          ? { clientId: resolvedClientId, packId: selectedPackId }
          : {
              packId: selectedPackId,
              customerEmail: data.profile.email,
              customerName: data.profile.name,
              customerPhone: data.profile.phone,
            },
        tenantId,
      );
      toast.success("Pack asignado");
      setSelectedPackId("");
      const newClientId = res.item?.clientId ?? resolvedClientId;
      setResolvedClientId(newClientId);
      loadClientPacks(newClientId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo asignar el pack");
    } finally {
      setAssigningPack(false);
    }
  };

  const rfmTone =
    data.rfmBucket === "active"
      ? "bg-emerald-500/15 text-emerald-600"
      : data.rfmBucket === "at_risk"
        ? "bg-amber-500/15 text-amber-600"
        : "bg-red-500/15 text-red-600";

  return (
    <div className="space-y-4" data-testid="customer-360">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <button type="button" onClick={onBack} className="hover:text-foreground hover:underline">
          Clientes
        </button>
        <span>/</span>
        <span className="font-medium text-foreground">
          {data.profile.name ?? data.profile.email ?? "Cliente"}
        </span>
      </div>
      <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {data.profile.name ?? data.profile.email ?? "Cliente"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {[data.profile.email, data.profile.phone].filter(Boolean).join(" · ") || "Sin contacto"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {waitlistEntry ? (
              <Link to="/app/lista-espera">
                <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/25">En lista de espera</Badge>
              </Link>
            ) : null}
            <Badge variant="outline">B2C</Badge>
            <Badge className={rfmTone}>{data.rfmLabel ?? RFM_BUCKET_LABELS[data.rfmBucket]}</Badge>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
          <div className="rounded-lg border border-border/40 px-3 py-2">
            <p className="text-[11px] uppercase text-muted-foreground">Pedidos</p>
            <p className="text-lg font-semibold tabular-nums">{data.orderCount}</p>
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2">
            <p className="text-[11px] uppercase text-muted-foreground">LTV</p>
            <p className="text-lg font-semibold tabular-nums">{fmtMoney(data.revenueLifetime)}</p>
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2">
            <p className="text-[11px] uppercase text-muted-foreground">Último pedido</p>
            <p className="text-sm font-medium">
              {data.lastOrderAt
                ? new Date(data.lastOrderAt).toLocaleDateString("es-AR")
                : "—"}
            </p>
            {data.daysSinceLastOrder != null && (
              <p className="text-xs text-muted-foreground">hace {data.daysSinceLastOrder} días</p>
            )}
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2">
            <p className="text-[11px] uppercase text-muted-foreground">Puntos loyalty</p>
            <p className="text-lg font-semibold tabular-nums">
              {data.loyaltyPoints != null ? data.loyaltyPoints : "—"}
            </p>
            {data.loyaltyPoints == null && (
              <p className="text-xs text-muted-foreground">Programa off o sin cuenta</p>
            )}
            <label className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={loyaltyOptIn}
                disabled={savingLoyaltyOptIn}
                onChange={(e) => void toggleLoyaltyOptIn(e.target.checked)}
              />
              Suma puntos
            </label>
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2" data-testid="customer-360-current-account">
            <p className="text-[11px] uppercase text-muted-foreground">Cta. corriente</p>
            {currentAccount?.enabled ? (
              <>
                <p className={`text-lg font-semibold tabular-nums ${currentAccount.balance < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {fmtMoney(currentAccount.balance)}
                </p>
                <p className="text-xs text-muted-foreground">{currentAccount.balance < 0 ? "Deuda" : "Saldo a favor"}</p>
              </>
            ) : (
              <p className="text-sm font-medium text-muted-foreground">No habilitada</p>
            )}
            {data.profile.email || data.profile.phone ? (
              <Link
                to="/app/clientes/cuenta-corriente"
                search={{
                  email: data.profile.email ?? undefined,
                  phone: data.profile.email ? undefined : (data.profile.phone ?? undefined),
                  name: data.profile.name ?? data.profile.email ?? data.profile.phone ?? undefined,
                }}
                className="text-xs text-primary underline"
              >
                Gestionar
              </Link>
            ) : null}
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2" data-testid="customer-360-nps">
            <p className="text-[11px] uppercase text-muted-foreground">Último NPS</p>
            <p className="text-lg font-semibold tabular-nums">
              {data.lastNpsScore != null ? `${data.lastNpsScore}/10` : "—"}
            </p>
            {data.lastNpsAt ? (
              <p className="text-xs text-muted-foreground">
                {new Date(data.lastNpsAt).toLocaleDateString("es-AR")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Sin envío NPS</p>
            )}
          </div>
          <div className="rounded-lg border border-border/40 px-3 py-2" data-testid="customer-360-review-clicks">
            <p className="text-[11px] uppercase text-muted-foreground">Clicks reseña Google</p>
            <p className="text-lg font-semibold tabular-nums">{data.reviewLinkClicks ?? 0}</p>
            {data.lastReviewLinkClickAt ? (
              <p className="text-xs text-muted-foreground">
                Último {new Date(data.lastReviewLinkClickAt).toLocaleDateString("es-AR")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Sin clicks registrados</p>
            )}
          </div>
        </div>
      </div>
      <Tabs defaultValue="resumen">
        <TabsList className="sticky top-[64px] z-10 flex h-auto flex-wrap gap-1 bg-background">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="cuenta-corriente">Cuenta corriente</TabsTrigger>
          <TabsTrigger value="contactos">Contactos ({contacts.length})</TabsTrigger>
          <TabsTrigger value="direcciones">Direcciones ({addresses.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="cuenta-corriente" className="space-y-4 pt-4">
          <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-360-current-account-tab">
            <h3 className="mb-3 text-sm font-semibold">Cuenta corriente</h3>
            {currentAccount?.enabled ? (
              <div className="flex items-center gap-4">
                <div>
                  <p className={`text-2xl font-semibold tabular-nums ${currentAccount.balance < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {fmtMoney(currentAccount.balance)}
                  </p>
                  <p className="text-xs text-muted-foreground">{currentAccount.balance < 0 ? "Deuda" : "Saldo a favor"}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No habilitada para este cliente.</p>
            )}
            {data.profile.email || data.profile.phone ? (
              <Link
                to="/app/clientes/cuenta-corriente"
                search={{
                  email: data.profile.email ?? undefined,
                  phone: data.profile.email ? undefined : (data.profile.phone ?? undefined),
                  name: data.profile.name ?? data.profile.email ?? data.profile.phone ?? undefined,
                }}
                className="mt-3 inline-block text-sm text-primary underline"
              >
                Gestionar cuenta corriente
              </Link>
            ) : null}
          </div>
        </TabsContent>
        <TabsContent value="contactos" className="pt-4">
      <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-contacts">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Contactos</h3>
          <Button size="sm" variant="outline" onClick={openNewContactDialog}>
            Agregar contacto
          </Button>
        </div>
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin contactos agregados.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <div className="flex items-center font-medium">
                    <span>{c.name} {c.lastName ?? ""}</span>
                    {c.isPrimary && <Badge className="ml-2 bg-primary/15 text-primary">Principal</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {CONTACT_TYPE_LABELS[c.contactType]}
                    {c.email ? ` · ${c.email}` : ""}
                    {c.phone ? ` · ${c.phone}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" className="text-xs text-primary underline" onClick={() => openEditContactDialog(c)}>
                    Editar
                  </button>
                  <button type="button" className="text-xs text-destructive underline" onClick={() => void deleteContact(c)}>
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
        </TabsContent>
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? "Editar contacto" : "Nuevo contacto"}</DialogTitle>
            <DialogDescription>Personas asociadas a este cliente (vendedor, administración, envíos, etc.).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="contact-name">Nombre *</Label>
              <Input id="contact-name" value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="contact-lastname">Apellido</Label>
              <Input id="contact-lastname" value={contactForm.lastName} onChange={(e) => setContactForm((f) => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="contact-email">Email</Label>
              <Input id="contact-email" type="email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="contact-phone">Teléfono</Label>
              <Input id="contact-phone" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={contactForm.contactType} onValueChange={(v) => setContactForm((f) => ({ ...f, contactType: v as CustomerContactType }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CONTACT_TYPE_LABELS) as CustomerContactType[]).map((t) => (
                    <SelectItem key={t} value={t}>{CONTACT_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={contactForm.isPrimary}
                onChange={(e) => setContactForm((f) => ({ ...f, isPrimary: e.target.checked }))}
              />
              Marcar como contacto principal
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Cancelar</Button>
            <Button disabled={!contactForm.name.trim() || savingContact} onClick={() => void saveContact()}>
              {editingContact ? "Guardar cambios" : "Agregar contacto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        <TabsContent value="direcciones" className="pt-4">
      <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-addresses">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Direcciones</h3>
          <Button size="sm" variant="outline" onClick={openNewAddressDialog}>
            Agregar dirección
          </Button>
        </div>
        {addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin direcciones agregadas.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {addresses.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <div className="flex items-center font-medium">
                    <span>{a.label || ADDRESS_TYPE_LABELS[a.addressType]}</span>
                    {a.isDefault && <Badge className="ml-2 bg-primary/15 text-primary">Principal</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {ADDRESS_TYPE_LABELS[a.addressType]} · {a.address}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" className="text-xs text-primary underline" onClick={() => openEditAddressDialog(a)}>
                    Editar
                  </button>
                  <button type="button" className="text-xs text-destructive underline" onClick={() => void deleteAddress(a)}>
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
        </TabsContent>
      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAddress ? "Editar dirección" : "Nueva dirección"}</DialogTitle>
            <DialogDescription>Direcciones asociadas a este cliente (fiscal, envío, facturación, etc.).</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="addr-street">Calle *</Label>
                <Input id="addr-street" value={addressForm.street} onChange={(e) => setAddressForm((f) => ({ ...f, street: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="addr-number">Número *</Label>
                <Input id="addr-number" value={addressForm.streetNumber} onChange={(e) => setAddressForm((f) => ({ ...f, streetNumber: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="addr-floor">Piso</Label>
                <Input id="addr-floor" value={addressForm.floor} onChange={(e) => setAddressForm((f) => ({ ...f, floor: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="addr-apt">Depto</Label>
                <Input id="addr-apt" value={addressForm.apartment} onChange={(e) => setAddressForm((f) => ({ ...f, apartment: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="addr-city">Localidad *</Label>
                <Input id="addr-city" value={addressForm.city} onChange={(e) => setAddressForm((f) => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="addr-province">Provincia *</Label>
                <Input id="addr-province" value={addressForm.province} onChange={(e) => setAddressForm((f) => ({ ...f, province: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label htmlFor="addr-postal">Código postal *</Label>
              <Input id="addr-postal" value={addressForm.postalCode} onChange={(e) => setAddressForm((f) => ({ ...f, postalCode: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="addr-label">Etiqueta</Label>
              <Input id="addr-label" placeholder="Ej: Depósito centro" value={addressForm.label} onChange={(e) => setAddressForm((f) => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="addr-notes">Notas</Label>
              <Input id="addr-notes" value={addressForm.notes} onChange={(e) => setAddressForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={addressForm.addressType} onValueChange={(v) => setAddressForm((f) => ({ ...f, addressType: v as CustomerAddressType }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ADDRESS_TYPE_LABELS) as CustomerAddressType[]).map((t) => (
                    <SelectItem key={t} value={t}>{ADDRESS_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={addressForm.isDefault}
                onChange={(e) => setAddressForm((f) => ({ ...f, isDefault: e.target.checked }))}
              />
              Marcar como dirección principal
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressDialogOpen(false)}>Cancelar</Button>
            <Button
              disabled={!addressForm.street.trim() || !addressForm.streetNumber.trim() || !addressForm.city.trim() || !addressForm.province.trim() || !addressForm.postalCode.trim() || savingAddress}
              onClick={() => void saveAddress()}
            >
              {editingAddress ? "Guardar cambios" : "Agregar dirección"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        <TabsContent value="resumen" className="space-y-4 pt-4">
      <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-internal-notes">
        <h3 className="mb-2 text-sm font-semibold">Notas internas</h3>
        <p className="mb-2 text-xs text-muted-foreground">Solo visibles para el equipo, nunca para el cliente.</p>
        <textarea
          className="w-full rounded-md border border-border/60 bg-background p-2 text-sm"
          rows={3}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          placeholder="Preferencias, acuerdos, incidentes…"
        />
        <Button size="sm" className="mt-2" disabled={savingNotes} onClick={() => void saveInternalNotes()}>
          Guardar notas
        </Button>
      </div>
      {showTurnos ? (
        <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-service-bookings">
          <h3 className="mb-3 text-sm font-semibold">Historial de turnos</h3>
          {turnos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin turnos para este email.</p>
          ) : (
            <ul className="divide-y divide-border/40">
              {turnos.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{t.serviceName ?? "Servicio"} · {t.bookingDate} {t.startTime}</p>
                    <p className="text-xs text-muted-foreground">{t.professionalName ?? ""} · {t.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      {showTurnos ? (
        <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-packs">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">Packs de sesiones</h3>
            <div className="flex items-center gap-2">
              <Select value={selectedPackId} onValueChange={setSelectedPackId}>
                <SelectTrigger className="h-8 w-[220px] text-xs">
                  <SelectValue placeholder="Elegir plantilla…" />
                </SelectTrigger>
                <SelectContent>
                  {sessionPacks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {p.sessionCount} sesiones
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!selectedPackId || assigningPack} onClick={() => void assignPack()}>
                Asignar pack
              </Button>
            </div>
          </div>
          {clientPacks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin packs asignados.</p>
          ) : (
            <ul className="divide-y divide-border/40">
              {clientPacks.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{p.packName ?? "Pack"}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.sessionsRemaining}/{p.sessionsTotal} sesiones restantes
                      {p.expiresAt ? ` · vence ${new Date(p.expiresAt).toLocaleDateString("es-AR")}` : ""}
                      {" · "}{p.paymentStatus === "paid" ? "Pagado" : p.paymentStatus === "refunded" ? "Reembolsado" : "Pendiente de pago"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h3 className="mb-3 text-sm font-semibold">Últimos pedidos</h3>
        {data.recentOrders.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin pedidos.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {data.recentOrders.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <p className="font-medium tabular-nums">#{o.id.slice(-6).toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(o.createdAt).toLocaleString("es-AR")} ·{" "}
                    {ORDER_STATUS_LABELS[o.status as OrderStatus] ?? o.status}
                  </p>
                </div>
                <span className="tabular-nums text-muted-foreground">{fmtMoney(o.total)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="rounded-xl border border-border/60 bg-card p-5" data-testid="customer-recent-invoices">
        <h3 className="mb-3 text-sm font-semibold">Últimas facturas</h3>
        {data.recentInvoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin facturas.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {data.recentInvoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <p className="font-medium">{inv.type ?? "Comprobante"} {inv.number ?? ""}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(inv.createdAt).toLocaleDateString("es-AR")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-muted-foreground">{inv.total != null ? fmtMoney(inv.total) : "—"}</span>
                  {inv.pdfUrl ? (
                    <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
                      PDF
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClientesPage() {
  const navigate = useNavigate();
  const tid = useWorkspaceTenantId();
  const { vista, detalle, cohort, nuevo } = Route.useSearch();
  const [storeCustomers, setStoreCustomers] = useState<StoreCustomer[]>([]);
  const [b2bUsers, setB2bUsers] = useState<InstitutionalUser[]>([]);
  const [channels, setChannels] = useState<B2bChannelOption[]>([]);
  const [approveChannel, setApproveChannel] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Customer360 | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(Boolean(nuevo));
  const [newClientForm, setNewClientForm] = useState({ name: "", email: "", phone: "" });
  const [newClientSaving, setNewClientSaving] = useState(false);

  useEffect(() => {
    if (nuevo) setNewClientOpen(true);
  }, [nuevo]);

  const closeNewClient = () => {
    setNewClientOpen(false);
    setNewClientForm({ name: "", email: "", phone: "" });
    if (nuevo) {
      void navigate({ to: "/app/clientes", search: { vista: "b2c", cohort, detalle: undefined, nuevo: undefined } });
    }
  };

  const createClient = async () => {
    if (!tid || !newClientForm.name.trim()) return;
    setNewClientSaving(true);
    try {
      await apiSendJson("/api/customers", "POST", {
        name: newClientForm.name,
        email: newClientForm.email || undefined,
        phone: newClientForm.phone || undefined,
      }, tid);
      toast.success("Cliente creado");
      closeNewClient();
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear el cliente");
    } finally {
      setNewClientSaving(false);
    }
  };

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [custRes, users, ch] = await Promise.all([
        apiGetJson<{ customers: StoreCustomer[] }>("/api/customers", tid),
        apiGetJson<InstitutionalUser[]>("/api/institutional/users", tid).catch(() => []),
        apiGetJson<B2bChannelOption[]>("/api/b2b/channels", tid).catch(() => []),
      ]);
      setStoreCustomers(custRes.customers ?? []);
      setB2bUsers(users);
      setChannels(ch);
    } catch {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!tid || !detalle || vista !== "b2c") {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    void apiGetJson<Customer360>(`/api/customers?key=${encodeURIComponent(detalle)}`, tid)
      .then(setDetail)
      .catch(() => {
        toast.error("No se pudo cargar la ficha del cliente");
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  }, [tid, detalle, vista]);

  const approveUser = async (userId: string, channelId?: string | null) => {
    if (!tid) return;
    try {
      await apiSendJson("/api/institutional/users", "PATCH", {
        userId,
        action: "approve",
        channelId: channelId ?? null,
      }, tid);
      toast.success("Usuario B2B aprobado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const rejectUser = async (userId: string) => {
    if (!tid) return;
    try {
      await apiSendJson("/api/institutional/users", "PATCH", { userId, action: "reject" }, tid);
      toast.success("Usuario rechazado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const pendingB2b = b2bUsers.filter((u) => u.status === "pending").length;

  const filteredStoreCustomers = storeCustomers.filter((c) =>
    matchesCustomerCohort(
      { orderCount: c.orderCount, lastOrderAt: c.lastOrderAt },
      cohort,
    ),
  );

  const setCohort = (next: CustomerCohortFilter) => {
    void navigate({
      to: "/app/clientes",
      search: { vista: "b2c", cohort: next, detalle },
    });
  };

  const storeCustomerColumns: DataTableColumn<StoreCustomer>[] = [
    { key: "nombre", header: "Nombre", render: (c) => c.customerName ?? "—" },
    { key: "email", header: "Email", render: (c) => c.customerEmail ?? "—" },
    { key: "telefono", header: "Teléfono", render: (c) => c.customerPhone ?? "—" },
    { key: "pedidos", header: "Pedidos", render: (c) => c.orderCount },
    {
      key: "cohort",
      header: "Cohorte",
      render: (c) => {
        const bucket = classifyRfmBucket(c.lastOrderAt);
        const isReturning = c.orderCount >= 2;
        return (
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="text-[10px] font-normal">
              {isReturning ? "Recurrente" : "Nuevo"}
            </Badge>
            <Badge
              variant="secondary"
              className={
                bucket === "active"
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[10px] font-normal"
                  : bucket === "at_risk"
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[10px] font-normal"
                    : "text-[10px] font-normal"
              }
            >
              {RFM_BUCKET_LABELS[bucket]}
            </Badge>
          </div>
        );
      },
    },
    {
      key: "ultimo",
      header: "Último pedido",
      render: (c) => new Date(c.lastOrderAt).toLocaleString("es-AR"),
    },
    {
      key: "primera",
      header: "Primera compra",
      render: (c) => new Date(c.firstOrderAt).toLocaleString("es-AR"),
    },
    {
      key: "ficha",
      header: "",
      className: "text-right",
      render: (c) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            void navigate({
              to: "/app/clientes",
              search: {
                vista: "b2c",
                detalle: c.contactKey,
                cohort,
              },
            })
          }
        >
          Ver ficha
        </Button>
      ),
    },
  ];

  const b2bColumns: DataTableColumn<InstitutionalUser>[] = [
    { key: "nombre", header: "Nombre", render: (u) => `${u.firstName} ${u.lastName}` },
    { key: "email", header: "Email", render: (u) => u.email },
    { key: "institucion", header: "Institución", render: (u) => u.institutionName ?? u.channelName ?? "—" },
    {
      key: "estado",
      header: "Estado",
      render: (u) => (
        <Badge
          variant={u.status === "approved" ? "default" : u.status === "pending" ? "secondary" : "outline"}
        >
          {u.status === "approved"
            ? "Aprobado"
            : u.status === "pending"
              ? "Pendiente"
              : u.status === "rejected"
                ? "Rechazado"
                : u.status}
        </Badge>
      ),
    },
    { key: "alta", header: "Alta", render: (u) => new Date(u.createdAt).toLocaleDateString("es-AR") },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (u) =>
        u.status === "pending" ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {channels.length > 0 && (
              <Select
                value={approveChannel[u.id] ?? channels[0]?.id}
                onValueChange={(v) => setApproveChannel((prev) => ({ ...prev, [u.id]: v }))}
              >
                <SelectTrigger className="w-36 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      {ch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" onClick={() => void approveUser(u.id, approveChannel[u.id] ?? channels[0]?.id)}>
              Aprobar
            </Button>
            <Button size="sm" variant="outline" onClick={() => void rejectUser(u.id)}>
              Rechazar
            </Button>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  if (loading && vista !== "hub") {
    return (
      <div>
        <div className="mb-3"><InlineCustomerAi entity="cliente" /></div>
        <MerchantExperienceBar screenId="clientes" />
        <CrudShellHeader title="Clientes" description="Compradores y usuarios del portal." />
        <Loading variant="list" rows={8} />
      </div>
    );
  }

  if (vista === "hub") {
    return (
      <div>
        <MerchantExperienceBar screenId="clientes" />
      <CrudShellHeader
        title="Clientes"
        description="Clientes, prospectos, fidelización y tarjetas de regalo"
          actions={
            <RenataLauncher
              entityType="clientes"
              intent="analyze"
              mode="button"
              label="Analizar con Renata"
            />
          }
        />
        <ClinicalPageShell offset="none" className="pt-6">
          <div className="mb-4">
            <p className="text-sm font-semibold text-foreground">¿Qué querés ver?</p>
          </div>
          <div className={MERCHANT_HUB_GRID} data-tour="workspace-hub">
            {CLIENTES_HUB_LINKS.map((link) => (
              <DashboardCard
                key={link.id}
                to={link.href}
                search={link.search}
                icon={link.icon}
                title={link.label}
                subtitle={link.description ?? ""}
              />
            ))}
          </div>
        </ClinicalPageShell>
      </div>
    );
  }

  const tabDefault = vista === "b2b" ? "b2b" : "tienda";

  return (
    <div>
      <ClientesTour />
      <MerchantExperienceBar screenId="clientes" />
      <CrudShellHeader
        title={vista === "b2b" ? "Clientes B2B" : "Clientes B2C"}
        description={vista === "b2b" ? "Portal institucional" : "Compradores de la tienda"}
        action={vista === "b2b" ? (
          <Button variant="outline" asChild>
            <Link to="/app/produccion" search={{} as never}><ExternalLink className="mr-2 h-4 w-4" />B2B / Mayorista</Link>
          </Button>
        ) : (
          <Button onClick={() => setNewClientOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />Nuevo cliente
          </Button>
        )}
      />
      <Dialog open={newClientOpen} onOpenChange={(open) => (open ? setNewClientOpen(true) : closeNewClient())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>Se guarda como contacto de compra, sin pedidos todavía.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="new-client-name">Nombre *</Label>
              <Input
                id="new-client-name"
                value={newClientForm.name}
                onChange={(e) => setNewClientForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre y apellido"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-client-email">Email</Label>
              <Input
                id="new-client-email"
                type="email"
                value={newClientForm.email}
                onChange={(e) => setNewClientForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="opcional"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-client-phone">Teléfono</Label>
              <Input
                id="new-client-phone"
                value={newClientForm.phone}
                onChange={(e) => setNewClientForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="opcional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeNewClient} disabled={newClientSaving}>Cancelar</Button>
            <Button onClick={() => void createClient()} disabled={newClientSaving || !newClientForm.name.trim()}>
              {newClientSaving ? "Creando…" : "Crear cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ClinicalPageShell offset="none" className="pt-6">
        <WorkspaceHubBack title="Clientes" onBack={() => void navigate({ to: "/app/clientes", search: { vista: "hub", detalle: undefined, cohort: undefined as never } })} />
        <Tabs defaultValue={tabDefault} key={tabDefault}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="tienda">Compradores tienda ({storeCustomers.length})</TabsTrigger>
            <TabsTrigger value="b2b">
              Usuarios B2B ({b2bUsers.length})
              {pendingB2b > 0 && (
                <Badge className="ml-2 h-5 px-1.5" variant="destructive">
                  {pendingB2b} pendientes
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tienda" className="mt-4">
            {detalle ? (
              detailLoading ? (
                <Loading variant="list" rows={4} />
              ) : detail ? (
                <Customer360Panel
                  data={detail}
                  onBack={() =>
                    void navigate({
                      to: "/app/clientes",
                      search: {
                        vista: "b2c",
                        cohort,
                        detalle: undefined,
                      },
                    })
                  }
                />
              ) : (
                <CrudEmptyState
                  title="Cliente no encontrado"
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void navigate({
                          to: "/app/clientes",
                          search: {
                            vista: "b2c",
                            cohort,
                            detalle: undefined,
                          },
                        })
                      }
                    >
                      Volver
                    </Button>
                  }
                />
              )
            ) : (
              <>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Personas que compraron o dejaron datos en pedidos online. Abrí la ficha 360
                    (RFM + LTV).
                  </p>
                  <div className="w-full sm:w-56" data-testid="customer-cohort-filter">
                    <label className="mb-1 block text-xs text-muted-foreground">Filtro cohorte</label>
                    <Select
                      value={cohort}
                      onValueChange={(v) => setCohort(parseCustomerCohort(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(CUSTOMER_COHORT_LABELS) as CustomerCohortFilter[]).map(
                          (k) => (
                            <SelectItem key={k} value={k}>
                              {CUSTOMER_COHORT_LABELS[k]}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="mb-2 text-xs text-muted-foreground" data-testid="customer-cohort-count">
                  Mostrando {filteredStoreCustomers.length} de {storeCustomers.length}
                  {cohort !== "all" ? ` · ${CUSTOMER_COHORT_LABELS[cohort]}` : ""}
                </p>
                <DataTable
                  columns={storeCustomerColumns}
                  rows={filteredStoreCustomers}
                  getRowKey={(c) => c.contactKey}
                  emptyState={<CrudEmptyState title="Sin compradores para este filtro" />}
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="b2b" className="mt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Usuarios que se registraron en el portal institucional. Aprobá o rechazá el acceso aquí.
            </p>
            <DataTable
              columns={b2bColumns}
              rows={b2bUsers}
              getRowKey={(u) => u.id}
              emptyState={
                <CrudEmptyState
                  title="Sin usuarios B2B"
                  description="Configurá canales para habilitar el registro institucional."
                  action={
                    <Button asChild variant="outline" size="sm">
                      <Link to="/app/produccion" search={{} as never}>B2B / Mayorista</Link>
                    </Button>
                  }
                />
              }
            />
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              Portal B2B y pedidos institucionales en
              <Link to="/app/produccion" search={{} as never} className="text-primary underline inline-flex items-center gap-0.5">
                B2B / Mayorista <ExternalLink className="h-3 w-3" />
              </Link>
            </p>
          </TabsContent>
        </Tabs>
      </ClinicalPageShell>
    </div>
  );
}
